import React, {forwardRef, useRef, useState} from 'react';
import {styled} from '@mui/material/styles';
import {
    DataGridPremium,
    Toolbar,
    ToolbarButton,
    ColumnsPanelTrigger,
    FilterPanelTrigger,
    ExportCsv,
    ExportExcel,
    ExportPrint,
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
    useGridApiContext
} from '@mui/x-data-grid-premium';
import {Box, useTheme, useMediaQuery, Tooltip, Badge, MenuItem, Menu, TextField, Button} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InputAdornment from '@mui/material/InputAdornment';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';

import StatefulDataGrid from './StatefulDataGrid';
import EmptyState from './EmptyState';

// Renders the canonical EmptyState inside the grid's no-rows area. The card's
// own border/background are dropped so it reads as content within the grid
// rather than a nested card.
function GridNoRowsOverlay({emptyState}) {
    return (
        <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', p: 3}}>
            <EmptyState
                {...emptyState}
                sx={{border: 'none', bgcolor: 'transparent', boxShadow: 'none', ...emptyState?.sx}}
            />
        </Box>
    );
}

const StyledQuickFilter = styled(QuickFilter)({
    display: 'grid',
    alignItems: 'center'
});

const StyledToolbarButton = styled(ToolbarButton)(({theme, ownerState}) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity'])
}));

const StyledTextField = styled(TextField)(({theme, ownerState}) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity'])
}));

function CustomToolbar(props) {
    const {disableColumnFilter = false, disableExport = false, showQuickFilter = true, searchExpanded = false, singleDownload = false, downloadLabel = 'Download', leftContent, csvOptions, printOptions, excelOptions, exportMenuItems = []} = props;
    const apiRef = useGridApiContext();
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuTriggerRef = useRef(null);

    // Custom reports (attendance, RDCO, RDCK) just need a single labelled Excel download —
    // the column/filter/search controls don't apply to a formatted, fixed-template export.
    if (singleDownload) {
        return (
            <Toolbar>
                {leftContent}
                <Box sx={{flexGrow: 1}} />
                {!disableExport && (
                    <ExportExcel
                        render={<Button size="small" startIcon={<FileDownloadIcon fontSize="small" />} />}
                        options={excelOptions}
                    >
                        {downloadLabel}
                    </ExportExcel>
                )}
            </Toolbar>
        );
    }

    return (
        <Toolbar>
            {leftContent}
            <Box sx={{flexGrow: 1}} />
            {!disableColumnFilter && (
                <Tooltip title={apiRef.current.getLocaleText('toolbarColumns')}>
                    <ColumnsPanelTrigger render={<ToolbarButton />}>
                        <ViewColumnIcon fontSize="small" />
                    </ColumnsPanelTrigger>
                </Tooltip>
            )}
            {!disableColumnFilter && (
                <Tooltip title={apiRef.current.getLocaleText('toolbarFilters')}>
                    <FilterPanelTrigger
                        render={(triggerProps, state) => (
                            <ToolbarButton {...triggerProps} color={state.filterCount > 0 ? 'primary' : 'default'}>
                                <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                                    <FilterListIcon fontSize="small" />
                                </Badge>
                            </ToolbarButton>
                        )}
                    />
                </Tooltip>
            )}
            {!disableExport && (
                <>
                    <Tooltip title={apiRef.current.getLocaleText('toolbarExport')}>
                        <ToolbarButton
                            ref={exportMenuTriggerRef}
                            aria-haspopup="true"
                            onClick={() => setExportMenuOpen(!exportMenuOpen)}
                        >
                            <FileDownloadIcon fontSize="small" />
                        </ToolbarButton>
                    </Tooltip>
                    {exportMenuOpen && (
                        <Menu
                            id="export-menu"
                            anchorEl={exportMenuTriggerRef.current}
                            open={exportMenuOpen}
                            onClose={() => setExportMenuOpen(false)}
                            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                            transformOrigin={{vertical: 'top', horizontal: 'right'}}
                            slotProps={{
                                list: {
                                    'aria-labelledby': 'export-menu-trigger'
                                }
                            }}
                        >
                            <ExportPrint
                                render={<MenuItem />}
                                options={printOptions}
                                onClick={() => setExportMenuOpen(false)}
                            >
                                {apiRef.current.getLocaleText('toolbarExportPrint')}
                            </ExportPrint>
                            <ExportCsv
                                render={<MenuItem />}
                                options={csvOptions}
                                onClick={() => setExportMenuOpen(false)}
                            >
                                {apiRef.current.getLocaleText('toolbarExportCSV')}
                            </ExportCsv>
                            <ExportExcel
                                render={<MenuItem />}
                                options={excelOptions}
                                onClick={() => setExportMenuOpen(false)}
                            >
                                {apiRef.current.getLocaleText('toolbarExportExcel')}
                            </ExportExcel>
                            {exportMenuItems.map((item, index) => (
                                <MenuItem
                                    key={index}
                                    disabled={item.disabled}
                                    onClick={() => {
                                        setExportMenuOpen(false);
                                        item.onClick?.();
                                    }}
                                >
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    )}
                </>
            )}
            {showQuickFilter && (
                // searchExpanded → controlled-open so it can't collapse on blur when
                // empty (the default uncontrolled behaviour); undefined keeps the
                // normal expand-on-click toggle for every other grid.
                <StyledQuickFilter expanded={searchExpanded || undefined}>
                    <QuickFilterTrigger
                        render={(triggerProps, state) => (
                            <Tooltip title="Search" enterDelay={0}>
                                <StyledToolbarButton
                                    {...triggerProps}
                                    ownerState={{expanded: state.expanded}}
                                    color="default"
                                    aria-disabled={state.expanded}
                                >
                                    <SearchIcon fontSize="small" />
                                </StyledToolbarButton>
                            </Tooltip>
                        )}
                    />
                    <QuickFilterControl
                        render={({ref, ...controlProps}, state) => (
                            <StyledTextField
                                {...controlProps}
                                ownerState={{expanded: state.expanded}}
                                inputRef={ref}
                                aria-label="Search"
                                placeholder="Search..."
                                size="small"
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: state.value ? (
                                            <InputAdornment position="end">
                                                <QuickFilterClear
                                                    edge="end"
                                                    size="small"
                                                    aria-label="Clear search"
                                                    material={{sx: {marginRight: -0.75}}}
                                                >
                                                    <CancelIcon fontSize="small" />
                                                </QuickFilterClear>
                                            </InputAdornment>
                                        ) : null,
                                        ...controlProps.slotProps?.input
                                    },
                                    ...controlProps.slotProps
                                }}
                            />
                        )}
                    />
                </StyledQuickFilter>
            )}
        </Toolbar>
    );
}

const SearchableDataGrid = forwardRef((props, ref) => {
    const {stateId, columns = [], rows = [], loading, hasConatiner = true, slotProps, showToolbar = true, slots: slotsProp, emptyState, sx, ...rest} = props;
    const {disableColumnFilter, disableExport} = rest;

    // Give the no-rows overlay room for the EmptyState card (autoHeight grids
    // otherwise collapse the overlay). Caller sx wins.
    const mergedSx = emptyState ? {'--DataGrid-overlayHeight': '260px', ...sx} : sx;
    const Component = stateId ? StatefulDataGrid : DataGridPremium;
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const {toolbar: toolbarSlotProps, loadingOverlay: loadingOverlaySlotProps, ...restSlotProps} = slotProps || {};
    const toolbar = {
        disableColumnFilter,
        disableExport,
        ...toolbarSlotProps
    };

    const content = (
        <Component
            ref={ref}
            disableRowSelectionExcludeModel
            stateId={stateId}
            showToolbar={!!showToolbar}
            slots={{
                toolbar: showToolbar ? CustomToolbar : undefined,
                ...(emptyState ? {noRowsOverlay: GridNoRowsOverlay} : {}),
                ...slotsProp
            }}
            slotProps={{
                toolbar,
                loadingOverlay: {
                    variant: 'skeleton',
                    noRowsVariant: 'skeleton',
                    ...loadingOverlaySlotProps
                },
                ...(emptyState ? {noRowsOverlay: {emptyState}} : {}),
                ...restSlotProps
            }}
            rows={rows}
            loading={loading}
            columns={columns.map(column => {
                return {
                    disableColumnMenu: isSmall,
                    ...column
                };
            })}
            sx={mergedSx}
            {...rest}
        />
    );

    if (!hasConatiner) {
        return content;
    }

    return (
        <Box sx={{width: '100%'}}>
            <Box style={{display: 'flex', flexDirection: 'column', minHeight: !rows || rows.length === 0 || loading ? 250 : 'auto'}}>
                {content}
            </Box>
        </Box>
    );
});

export default SearchableDataGrid;
